import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Reveal } from "@/components/Reveal";
import { useSeo } from "@/hooks/useSeo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TERRITORIES = [
  "Quiet Luxury",
  "One of One",
  "Bespoke",
  "Collectible Design",
  "Automotive Art",
];

// Perspective 0 is the Manifesto — it stands outside the numbered series.
const perspectiveLabel = (p) =>
  p.perspective_number === 0
    ? "THE MANIFESTO"
    : `PERSPECTIVE ${String(p.perspective_number).padStart(3, "0")}`;

export default function PerspectivesIndex() {
  const [items, setItems] = useState([]);
  const [territory, setTerritory] = useState(null);

  // Perspectives pages always sit on solid deep black — no carbon texture.
  useEffect(() => {
    const prev = document.body.style.background;
    document.body.style.background = "#070708";
    return () => {
      document.body.style.background = prev;
    };
  }, []);

  useSeo({
    title: "Perspectives — FMF",
    description:
      "An editorial territory on quiet luxury, one-of-one objects, bespoke creation, collectible design and automotive art. A Perspective by FMF.",
    path: "/perspectives",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "Perspectives — FMF",
      description:
        "An editorial territory on quiet luxury, one-of-one objects, bespoke creation, collectible design and automotive art.",
    },
  });

  useEffect(() => {
    axios
      .get(`${API}/perspectives`)
      .then((r) => setItems(r.data))
      .catch(() => {});
  }, []);

  const visible = territory
    ? items.filter((p) => p.category === territory)
    : items;

  return (
    <main data-testid="perspectives-index" className="relative min-h-screen px-6 md:px-14 pt-40 pb-[16vh]" style={{ background: "#070708" }}>
      <Reveal>
        <Link
          data-testid="perspectives-home-link"
          to="/"
          className="flex justify-center"
        >
          <img src="/fmf-logo-white.png" alt="FMF" className="w-14 opacity-90" />
        </Link>
      </Reveal>
      <Reveal delay={0.1}>
        <p
          data-testid="perspectives-kicker"
          className="mt-16 font-mono-fmf text-[13px] uppercase tracking-[0.5em] text-[var(--dim)] text-center"
        >
          FMF — Editorial
        </p>
      </Reveal>
      <Reveal delay={0.2}>
        <h1
          data-testid="perspectives-title"
          className="mt-10 text-center font-editorial font-light text-5xl md:text-7xl text-white"
        >
          Perspectives
        </h1>
      </Reveal>

      <Reveal delay={0.4}>
        <nav
          data-testid="perspectives-territories"
          className="mt-[10vh] flex flex-wrap justify-center gap-x-10 gap-y-4"
        >
          <button
            data-testid="territory-all"
            onClick={() => setTerritory(null)}
            className={`font-mono-fmf text-[13px] uppercase tracking-[0.3em] transition-colors duration-700 ${
              territory === null ? "text-white" : "text-[var(--dim)] hover:text-white"
            }`}
          >
            All
          </button>
          {TERRITORIES.map((t, i) => (
            <button
              key={t}
              data-testid={`territory-${i}`}
              onClick={() => setTerritory(territory === t ? null : t)}
              className={`font-mono-fmf text-[13px] uppercase tracking-[0.3em] transition-colors duration-700 ${
                territory === t ? "text-white" : "text-[var(--dim)] hover:text-white"
              }`}
            >
              {String(i + 1).padStart(2, "0")} — {t}
            </button>
          ))}
        </nav>
      </Reveal>

      <div className="mt-[12vh] max-w-6xl mx-auto">
        {visible.map((p, i) => (
          <Reveal key={p.slug} delay={0.05}>
            <Link
              data-testid={`perspective-link-${p.perspective_number}`}
              to={
                p.slug === "noise-is-not-luxury"
                  ? "/noise-is-not-luxury"
                  : `/perspectives/${p.slug}`
              }
              className={`group block py-14 md:py-20 ${
                i % 2 === 1 ? "md:text-right" : "text-left"
              }`}
            >
              <span className="block font-mono-fmf text-[13px] tracking-[0.5em] text-[var(--dim)]">
                {perspectiveLabel(p)}
              </span>
              <h2 className="mt-6 font-editorial font-light text-3xl md:text-5xl lg:text-6xl leading-tight text-white transition-opacity duration-700 group-hover:opacity-60">
                {p.title}
              </h2>
              <p
                className={`mt-5 font-editorial italic text-lg md:text-xl text-[var(--bone-dim)] max-w-xl ${
                  i % 2 === 1 ? "md:ml-auto" : ""
                }`}
              >
                {p.subtitle}
              </p>
              <p className="mt-8 font-mono-fmf text-[12px] uppercase tracking-[0.3em] text-[var(--dim)]">
                {p.category} · {p.reading_time} · {p.publication_date}
              </p>
            </Link>
          </Reveal>
        ))}
      </div>

      <motion.p
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 2 }}
        className="mt-[14vh] text-center font-mono-fmf text-[12px] uppercase tracking-[0.4em] text-[var(--dim)]"
      >
        A Perspective by FMF.
      </motion.p>
    </main>
  );
}
